import { newIdempotencyKey } from "@/api/client";

// Possible outcomes of a share attempt.
export const SHARE_RESULT = {
  SHARED: "shared",     // confirmed in the room (fresh post or already-shared dedupe)
  FAILED: "failed",     // POST rejected/uncertain — stays retryable, key retained
  INFLIGHT: "inflight", // a send for this story is already in progress — ignored (no dup)
  INVALID: "invalid",   // no id / empty body — nothing sent
};

// Framework-agnostic controller that shares attributed headlines to the room
// through the SAME authorized chat POST path. It guarantees:
//  - a story is marked shared ONLY after a confirmed POST success,
//  - a failed/uncertain send KEEPS its idempotency key so a manual retry
//    reuses it (the backend de-dupes if the first attempt actually landed),
//  - there is NO automatic resend,
//  - rapid repeated clicks on the same story cannot duplicate the send,
//  - different stories are tracked independently (never falsely accepted).
// State is per-room; create a fresh controller per roomId so nothing leaks
// across games/rooms.
export function createStorySharer({ postFn, buildBody, genKey = newIdempotencyKey }) {
  const keys = new Map();     // storyId -> idempotency key (retained until confirmed success)
  const inflight = new Map(); // storyId -> in-flight Promise<result> (observed by late subscribers)
  const shared = new Set();   // storyId confirmed in the room

  const isShared = (id) => shared.has(id);
  const isInflight = (id) => inflight.has(id);

  // A duplicate call while a send is in flight returns the SAME promise so a
  // later subscriber (e.g. after A→B→A tab/game changes) observes the original
  // outcome without issuing another POST or minting a new idempotency key.
  async function share(item) {
    const id = item && item.id;
    if (!id) return { id, status: SHARE_RESULT.INVALID };
    if (shared.has(id)) return { id, status: SHARE_RESULT.SHARED, deduped: true };
    const existing = inflight.get(id);
    if (existing) return existing;

    const body = buildBody(item);
    if (!body || !body.trim()) return { id, status: SHARE_RESULT.INVALID };

    const key = keys.get(id) || genKey();
    keys.set(id, key); // persist so a retry of an uncertain send reuses the key
    const p = (async () => {
      try {
        await postFn({ body, idempotency_key: key });
        shared.add(id);
        keys.delete(id); // confirmed — key no longer needed
        return { id, status: SHARE_RESULT.SHARED, key };
      } catch {
        return { id, status: SHARE_RESULT.FAILED, key }; // key kept for same-key retry
      } finally {
        inflight.delete(id);
      }
    })();
    inflight.set(id, p);
    return p;
  }

  return { share, isShared, isInflight };
}
